import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    NIL_TEXT,
    roleLabelKey,
    TEMPLATES_EMPTY_COPY,
    type OperationRow,
    type TemplatesEmptyState,
} from '../../templatesMeta';
import { OperationRecipe } from '../operationRecipe';
import { OperationCategoryChip } from '../../../components';
import { OperationStatusChip } from '../operationStatusChip';

type OperationTableProps = {
    ariaLabel: string;
    locale: LocaleCode;
    rows: OperationRow[];
    emptyState: TemplatesEmptyState;
    /**
     * The catalog is still in flight. The table keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "no operations".
     */
    isLoading?: boolean;
    onEdit: (key: string) => void;
    /**
     * Show what the operation posts, read-only. Offered on every template row
     * whatever the role, and for the same reason as the history: reading a
     * recipe is not changing one, and until this existed the only way to see one
     * was to open the editor.
     */
    onPreview: (key: string) => void;
    /**
     * Open the version history. Offered on every template row regardless of
     * role: reading why an entry posted where it did is not editing, and a
     * BranchAdmin who cannot change a recipe still has to be able to explain
     * one.
     */
    onOpenHistory: (key: string) => void;
    /** Retire an operation, or bring one back. Absent for a read-only session. */
    onSetBlocked: ((key: string, blocked: boolean) => void) | null;
};

const SKELETON_ROW_COUNT = 6;

/**
 * The operation catalog.
 *
 * A HeroUI Pro `DataGrid`, which renders a real `<table>` underneath — genuine
 * `columnheader`s and a `rowheader` for the name. Its accessible name is
 * `aria-label`; DataGrid has no `<caption>` slot.
 *
 * **The two kinds of row have to be unmistakable at a glance**, and three cells
 * do that work together rather than one: the chip reads **System** instead of a
 * status, the version and pairs columns hold an en dash because a code-defined
 * flow has neither, and the actions cell offers a recipe instead of an Edit
 * button. A system row is not greyed out — it is not disabled, it is a different
 * kind of thing, and muting it would suggest it had been switched off.
 *
 * Edit is rendered **disabled with a tooltip that says why** for a read-only
 * session rather than hidden. A BranchAdmin can see that editing exists and that
 * it is not theirs; removing the button would leave them wondering whether the
 * catalog is editable at all. Block/unblock is the opposite — it is *absent* for
 * them, because it is a single destructive-shaped action with nothing to explain
 * about a state they cannot reach.
 */
export const OperationTable: React.FC<OperationTableProps> = ({
    ariaLabel,
    locale,
    rows,
    emptyState,
    isLoading = false,
    onEdit,
    onPreview,
    onOpenHistory,
    onSetBlocked,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<OperationRow>[]>(
        () => [
            {
                id: 'operation',
                header: t('operations.columns.operation'),
                isRowHeader: true,
                minWidth: 230,
                cell: (row) => (
                    <span className="flex flex-col gap-0.5">
                        <span className="font-medium text-foreground">
                            {locale === 'ka' ? row.nameKa : row.nameEn}
                        </span>
                        <span className="text-xs text-muted">
                            {locale === 'ka' ? row.nameEn : row.nameKa}
                        </span>
                    </span>
                ),
            },
            {
                id: 'status',
                header: t('operations.columns.status'),
                minWidth: 110,
                cell: (row) => (
                    <OperationStatusChip slot={row.key} status={row.status} />
                ),
            },
            {
                id: 'category',
                header: t('operations.columns.category'),
                minWidth: 140,
                cell: (row) => (
                    <OperationCategoryChip
                        category={row.category}
                        locale={locale}
                        slot={row.key}
                    />
                ),
            },
            {
                id: 'version',
                header: t('operations.columns.version'),
                minWidth: 150,
                cell: (row) => (
                    <span className="flex flex-col gap-0.5">
                        <span
                            className={cn(
                                'whitespace-nowrap tabular-nums',
                                row.versionText === NIL_TEXT
                                    ? 'text-muted'
                                    : 'text-foreground',
                            )}
                        >
                            {row.versionText}
                        </span>
                        {row.effectiveFromText === NIL_TEXT ? null : (
                            <span className="whitespace-nowrap text-xs text-muted tabular-nums">
                                {t('operations.effectiveFrom', {
                                    date: row.effectiveFromText,
                                })}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                id: 'pairs',
                header: t('operations.columns.pairs'),
                align: 'end',
                minWidth: 90,
                cell: (row) => (
                    <span
                        className={cn(
                            'tabular-nums',
                            row.pairsText === NIL_TEXT
                                ? 'text-muted'
                                : 'text-foreground',
                        )}
                    >
                        {row.pairsText}
                    </span>
                ),
            },
            {
                id: 'roles',
                header: t('operations.columns.roles'),
                minWidth: 210,
                cell: (row) =>
                    row.rolesAllowed.length === 0 ? (
                        // A system row is not recorded by anybody — it posts
                        // itself off a sale or a run — so this is an en dash and
                        // not an empty cell, which would read as "nobody, and
                        // that is a problem".
                        <span className="text-muted">{NIL_TEXT}</span>
                    ) : (
                        <span className="flex flex-wrap gap-1">
                            {row.rolesAllowed.map((role) => (
                                <span
                                    className="inline-flex items-center rounded-lg bg-surface-secondary px-2 py-0.5 text-xs text-muted"
                                    key={role}
                                    {...getNodeTestIdProp(
                                        `operations-role-chip-${row.key}-${role}`,
                                    )}
                                >
                                    {t(roleLabelKey(role))}
                                </span>
                            ))}
                        </span>
                    ),
            },
            {
                // Muted throughout, including when it has a value: this column
                // explains the row order, it is not a figure anyone acts on.
                // Its header names the window, which is what makes the order
                // legible rather than mysterious.
                id: 'recordCount',
                header: t('operations.columns.recordCount'),
                align: 'end',
                minWidth: 110,
                cell: (row) => (
                    <span
                        className="whitespace-nowrap tabular-nums text-muted"
                        {...getNodeTestIdProp(
                            `operations-record-count-${row.key}`,
                        )}
                    >
                        {row.recordCountText}
                    </span>
                ),
            },
            {
                id: 'lastUsed',
                header: t('operations.columns.lastUsed'),
                minWidth: 120,
                cell: (row) => (
                    <span
                        className={cn(
                            'whitespace-nowrap tabular-nums',
                            row.lastUsedText === NIL_TEXT
                                ? 'text-muted'
                                : 'text-foreground',
                        )}
                    >
                        {row.lastUsedText}
                    </span>
                ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('operations.columns.actions')}
                    </span>
                ),
                align: 'end',
                minWidth: 190,
                // The accessible names live in visually hidden spans, not
                // `aria-label`: HeroUI's Tooltip wrapper drops an `aria-label`
                // set on its trigger Button, leaving an icon-only trigger
                // nameless. Naming the row keeps the buttons distinct in a
                // screen reader's element list.
                cell: (row) =>
                    row.kind === 'SYSTEM' ? (
                        <span className="flex justify-end">
                            <OperationRecipe
                                name={locale === 'ka' ? row.nameKa : row.nameEn}
                                slot={row.key}
                                steps={row.recipe}
                            />
                        </span>
                    ) : (
                        <span className="flex items-center justify-end gap-1">
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onPreview(row.key)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operations-row-preview-${row.key}`,
                                    )}
                                >
                                    <Icon aria-hidden icon="gravity-ui:eye" />
                                    <span className="sr-only">
                                        {t('operations.preview.triggerNamed', {
                                            name:
                                                locale === 'ka'
                                                    ? row.nameKa
                                                    : row.nameEn,
                                        })}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>{t('operations.preview.trigger')}</p>
                                </Tooltip.Content>
                            </Tooltip>
                            <Tooltip delay={0}>
                                <Button
                                    isIconOnly
                                    onPress={() => onOpenHistory(row.key)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operations-row-history-${row.key}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:clock-arrow-rotate-left"
                                    />
                                    <span className="sr-only">
                                        {t('operations.actions.historyNamed', {
                                            name:
                                                locale === 'ka'
                                                    ? row.nameKa
                                                    : row.nameEn,
                                        })}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>{t('operations.actions.history')}</p>
                                </Tooltip.Content>
                            </Tooltip>
                            <Tooltip delay={0}>
                                <Button
                                    isDisabled={!row.isEditable}
                                    isIconOnly
                                    onPress={() => onEdit(row.key)}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operations-row-edit-${row.key}`,
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        icon="gravity-ui:pencil"
                                    />
                                    <span className="sr-only">
                                        {t('operations.actions.editNamed', {
                                            name:
                                                locale === 'ka'
                                                    ? row.nameKa
                                                    : row.nameEn,
                                        })}
                                    </span>
                                </Button>
                                <Tooltip.Content>
                                    <p>
                                        {row.isEditable
                                            ? t('operations.actions.edit')
                                            : t(
                                                  'operations.actions.editReadOnly',
                                              )}
                                    </p>
                                </Tooltip.Content>
                            </Tooltip>
                            {onSetBlocked ? (
                                <Tooltip delay={0}>
                                    <Button
                                        isIconOnly
                                        onPress={() =>
                                            onSetBlocked(
                                                row.key,
                                                row.status !== 'BLOCKED',
                                            )
                                        }
                                        size="sm"
                                        variant="ghost"
                                        {...getNodeTestIdProp(
                                            `operations-row-block-${row.key}`,
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            icon={
                                                row.status === 'BLOCKED'
                                                    ? 'gravity-ui:circle-check'
                                                    : 'gravity-ui:ban'
                                            }
                                        />
                                        <span className="sr-only">
                                            {t(
                                                row.status === 'BLOCKED'
                                                    ? 'operations.actions.unblockNamed'
                                                    : 'operations.actions.blockNamed',
                                                {
                                                    name:
                                                        locale === 'ka'
                                                            ? row.nameKa
                                                            : row.nameEn,
                                                },
                                            )}
                                        </span>
                                    </Button>
                                    <Tooltip.Content>
                                        <p>
                                            {t(
                                                row.status === 'BLOCKED'
                                                    ? 'operations.actions.unblock'
                                                    : 'operations.actions.block',
                                            )}
                                        </p>
                                    </Tooltip.Content>
                                </Tooltip>
                            ) : null}
                        </span>
                    ),
            },
        ],
        [locale, onEdit, onPreview, onOpenHistory, onSetBlocked, t],
    );

    if (!isLoading && emptyState !== 'NONE') {
        const copy = TEMPLATES_EMPTY_COPY[emptyState];
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-14 text-center"
                {...getNodeTestIdProp(`operations-empty-${emptyState}`)}
            >
                <Icon
                    aria-hidden
                    className="size-8 text-muted"
                    icon={copy.icon}
                />
                <span className="text-sm font-medium text-foreground">
                    {t(copy.titleKey)}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t(copy.bodyKey)}
                </span>
            </div>
        );
    }

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the table's
    // testid has nowhere to land on the grid itself.
    return (
        <div className="contents" {...getNodeTestIdProp('operations-table')}>
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[980px]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'operations',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<OperationRow>
                    aria-label={ariaLabel}
                    className="ledger-data-grid"
                    columns={columns}
                    contentClassName="min-w-[980px]"
                    data={rows}
                    getRowId={(row) => row.key}
                />
            )}
        </div>
    );
};
